import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Persists a list screen's filters + pagination in the URL query string, so
 * navigating into a detail screen and clicking back restores the exact same
 * filtered/paginated view instead of resetting to defaults. Mirrors the
 * pattern already proven in layouts/patientList/index.js: reads are a
 * one-off manual parse of window.location.search (not the reactive
 * useSearchParams() value), writes go through setSearchParams in replace
 * mode so filter/page changes don't spam browser history.
 *
 * writeParams merges into whatever is already in the URL (by key) rather
 * than replacing the whole query string — a screen with two independent
 * filter sections (e.g. a patient search plus a separate procedure list,
 * each with its own key prefix) can call writeParams from each section
 * without one wiping out the other's params.
 *
 * Usage:
 *   const { readParams, writeParams } = useListUrlState();
 *   // on mount:
 *   const params = readParams();
 *   if (params) { const page = parseInt(params.get('page') || '1', 10); ... }
 *   // whenever filters/page change, right before fetching:
 *   writeParams({ search, status, page, limit });
 */
export function useListUrlState() {
  const [, setSearchParams] = useSearchParams();

  // Stable identities (setSearchParams itself is stable) so callers can
  // safely put these in a useCallback/useEffect dependency array without
  // triggering a re-run on every render.
  const readParams = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    return params.toString() ? params : null;
  }, []);

  const writeParams = useCallback((values) => {
    const params = new URLSearchParams(window.location.search);
    Object.entries(values).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        params.delete(key);
      } else {
        params.set(key, String(value));
      }
    });
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  return { readParams, writeParams };
}
